import {Dispatch, SetStateAction, useEffect} from 'react';

import {isProcedureRunDesignFlowchartNode} from './ProcedureRunDesignFlowchartNode';
import type {ProcedureRunDesignFlowchartLayoutNode} from './useProcedureRunDesignFlowchartLayout';

export interface UseProcedureRunDesignFlowchartNodeRemovalStateArgs {
  pendingRemovalNodeIds: ReadonlySet<string>;
  removedNodeIds: ReadonlySet<string>;
  initialColumnNodes: Array<ProcedureRunDesignFlowchartLayoutNode>;
  initialRows: Array<Array<ProcedureRunDesignFlowchartLayoutNode>>;
  setNodes: Dispatch<SetStateAction<Array<ProcedureRunDesignFlowchartLayoutNode>>>;
}

export function applyRemovalState(
  nodes: Array<ProcedureRunDesignFlowchartLayoutNode>,
  pendingRemovalNodeIds: ReadonlySet<string>,
  removedNodeIds: ReadonlySet<string>
): Array<ProcedureRunDesignFlowchartLayoutNode> {
  const updatedNodes = nodes.map((node) => {
    if (!isProcedureRunDesignFlowchartNode(node)) {
      return node;
    }
    const isPendingRemoval = pendingRemovalNodeIds.has(node.id);
    const isRemoved = removedNodeIds.has(node.id);
    if (node.data.isPendingRemoval === isPendingRemoval && node.data.isRemoved === isRemoved) {
      return node;
    }
    return {...node, data: {...node.data, isPendingRemoval, isRemoved}};
  });
  // Only return a new array if any of the nodes have been updated, otherwise return the original array
  return updatedNodes.some((node, i) => node !== nodes[i]) ? updatedNodes : nodes;
}

export function useProcedureRunDesignFlowchartNodeRemovalState({
  pendingRemovalNodeIds,
  removedNodeIds,
  initialColumnNodes,
  initialRows,
  setNodes,
}: UseProcedureRunDesignFlowchartNodeRemovalStateArgs): void {
  useEffect(() => {
    setNodes((currentNodes) => applyRemovalState(currentNodes, pendingRemovalNodeIds, removedNodeIds));
  }, [pendingRemovalNodeIds, removedNodeIds, initialColumnNodes, initialRows, setNodes]);
}
